import { animate, state, style, transition, trigger } from "@angular/animations";
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, OnInit, QueryList, ViewChildren } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { PermitType } from "@ha/data/basic";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Navigate } from "@ngxs/router-plugin";
import { Actions, ofActionSuccessful, Select, Store } from "@ngxs/store";
import { BehaviorSubject, delay, fromEvent, Observable, of, repeat, take } from "rxjs";
import { debounceTime } from "rxjs/operators";

import { HaModalService } from "@ha/ui/common";
import { PwaState } from "@ha/util/pwa";

import { PermitCreateActions } from "../../permit-create/states/permit-create.actions";
import { PermitQuestionActions } from "../../permit-create/states/permit-question.actions";
import { PermitListItem } from "../../permit-list/models/permit-list-item.model";
import { PermitListType } from "../../permit-list/models/permit-list-type.enum";
import { PermitListActions } from "../../permit-list/states/permit-list.actions";
import { PermitListSelectors } from "../../permit-list/states/permit-list.selectors";
import { PermitPaths } from "../../permit-paths";
import { PermitTeamActions } from "../../permit-team/states/permit-team.action";
import { PermitStepActions } from "../../permit/states/permit-step.action";
import { PermitRequest } from "../../shared/models/permit-request.model";
import { PermitToSign } from "../../shared/models/permit-to-sign.model";
import { PermitUserActions } from "../../shared/states/permit-user.action";
import { PermitUserSelectors } from "../../shared/states/permit-user.selectors";
import { SubscriptionInvitationAnswerComponent, SubscriptionInviteAnswerComponentData } from "../../subscriptions/components/subscription-invitation-answer-modal/subscription-invitation-answer.component";
import { SubscriptionInvitation } from "../../subscriptions/models/subscription-invitation";
import { SubscriptionsActions } from "../../subscriptions/states/subscriptions.actions";
import { SubscriptionsSelectors } from "../../subscriptions/states/subscriptions.selectors";
import { PermitCreateModalComponent, PermitCreateModalData, PermitCreateMode } from "./permit-create-modal/permit-create-modal.component";
import { PermitRootFilterModalComponent } from "./permit-root-filter-modal/permit-root-filter-modal.component";
import { SignPersonModalComponent, SignPersonModalComponentData } from "./sign-person-modal/sign-person-modal.component";

@UntilDestroy()
@Component({
    selector: "pmt-root",
    templateUrl: "permit-root.component.html",
    styleUrls: ["permit-root.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [trigger("tabIndicator", [
        state(
            "*",
            style({
                transform: "{{transform}}",
                width: "{{width}}px",
            }),
            { params: { transform: "translateX(0)", width: 0 } },
        ),
        transition("* => *", [animate("0.3s ease")]),
    ])],
})
export class PermitRootComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChildren("tabItem")
    tabItems!: QueryList<ElementRef>;

    @Select(PermitUserSelectors.haveAccessToPermit)
    public haveAccessToPermit$!: Observable<boolean>;

    @Select(SubscriptionsSelectors.hasActiveSubscription)
    public hasActiveSubscription$!: Observable<boolean>;

    @Select(SubscriptionsSelectors.invitations)
    public invitations$!: Observable<SubscriptionInvitation[]>;

    @Select(PermitUserSelectors.isHotworkerOrPremiumIssuer)
    public isHotworkerOrPremiumIssuer$!: Observable<boolean>;

    @Select(PermitUserSelectors.isPremiumSuperintendent)
    public isPremiumSuperintendent$!: Observable<boolean>;

    @Select(PermitUserSelectors.isPremiumIssuer)
    public isPremiumIssuer$!: Observable<boolean>;

    @Select(PermitUserSelectors.hasValidCertificate)
    public hasValidCertificate$!: Observable<boolean>;

    @Select(PermitUserSelectors.isHotWorker)
    public isHotWorker$!: Observable<boolean>;

    @Select(PermitUserSelectors.showCreateDemoButton)
    public showCreateDemoButton$!: Observable<boolean>;

    @Select(PermitListSelectors.completedPermits)
    public completedPermits$!: Observable<PermitListItem[]>;

    @Select(PermitListSelectors.ongoingPermits)
    public ongoingPermits$!: Observable<PermitListItem[]>;

    @Select(PermitListSelectors.filteredPermits)
    public filteredPermits$!: Observable<PermitListItem[]>;

    @Select(PermitListSelectors.plannedPermits)
    public plannedPermits$!: Observable<PermitListItem[]>;

    @Select(PermitListSelectors.showAllSubscriptionPermits)
    public showAllSubscriptionPermits$: Observable<false>;

    @Select(PermitListSelectors.selectedWorkplaces)
    public selectedWorkplaces$!: Observable<string[]>;

    @Select(PwaState.isMobile)
    public isMobile$!: Observable<boolean>;

    @Select(PermitUserSelectors.permitsToSign)
    public permitsToSign$!: Observable<PermitToSign[]>;

    @Select(PermitUserSelectors.permitsToClaim)
    public permitsToClaim$!: Observable<PermitToSign[]>;

    @Select(PermitUserSelectors.permitRequests)
    public permitRequests$!: Observable<PermitRequest[]>;

    public PermitType = PermitType;

    public tabIndicatorState = {
        value: 0,
        params: {
            transform: "translateX(0)",
            width: 0,
        },
    };
    public PermitListType = PermitListType;
    public selectedListType = PermitListType.Ongoing;
    public PermitPaths = PermitPaths;
    public PermitCreateMode = PermitCreateMode;

    public loading$ = new BehaviorSubject<boolean>(true);
    public showAllSubscriptionPermits = false;
    public selectedCreateMode: PermitCreateMode = PermitCreateMode.Base;

    private resizeObserver: ResizeObserver | null = null;

    private readonly SHOW_ALL_SUBSCRIPTION_PERMITS_KEY = "showSubscriptionPermits";

    constructor(
        private store: Store,
        private modalService: HaModalService,
        private actions$: Actions,
        private elementRef: ElementRef,
        private ngZone: NgZone,
        private cdr: ChangeDetectorRef,
        private route: ActivatedRoute,
    ) {}

    public ngOnInit(): void {
        const savedPreference = localStorage.getItem(this.SHOW_ALL_SUBSCRIPTION_PERMITS_KEY);
        if (savedPreference !== null) {
            this.showAllSubscriptionPermits = JSON.parse(savedPreference);
        }

        this.store.dispatch(new PermitUserActions.GetCurrentPerson());

        this.actions$.pipe(untilDestroyed(this), ofActionSuccessful(PermitUserActions.GetCurrentPerson)).subscribe(
            () => {
                if (this.store.selectSnapshot(PermitUserSelectors.currentPerson)) {
                    this.store.dispatch(new PermitListActions.GetAllOngoing()).subscribe(() => {
                        this.loading$.next(false);
                    });
                    this.store.dispatch(new PermitListActions.GetAllPlanned());
                    this.store.dispatch(new SubscriptionsActions.GetActiveAndPendingSubscriptions());
                    this.store.dispatch(new PermitUserActions.GetPermitsToSign());
                    this.store.dispatch(new PermitUserActions.GetPermitRequests());
                }
            },
        );

        this.actions$.pipe(ofActionSuccessful(PermitTeamActions.SignPermitWithSignature)).pipe(untilDestroyed(this))
            .subscribe(() => {
                this.store.dispatch(new PermitUserActions.GetCurrentPerson());
            });

        this.actions$.pipe(
            ofActionSuccessful(SubscriptionsActions.GetActiveAndPendingSubscriptions),
            untilDestroyed(this),
        ).subscribe(() => {
            const showPendingSubscription = this.route.snapshot.queryParamMap.get("showPendingSubscription");
            if (showPendingSubscription === "true") {
                this.openConnectionRequest();
            }
        });

        this.actions$.pipe(ofActionSuccessful(PermitUserActions.GetPermitsToSign), untilDestroyed(this)).subscribe(
            () => {
                const showDelegation = this.route.snapshot.queryParamMap.get("showDelegation");
                if (showDelegation === "true") {
                    const permitsToClaim = this.store.selectSnapshot(PermitUserSelectors.permitsToClaim);
                    if (permitsToClaim && permitsToClaim.length > 0) {
                        this.signPermit(permitsToClaim[0], true);
                    }
                }
            },
        );

        // Get current person every 60 seconds.
        // Update current person and also to trigger fetching every thing else like permits to sign and invitations.
        of(null).pipe(delay(60 * 1000), repeat(), untilDestroyed(this)).subscribe(() => {
            this.store.dispatch(new PermitUserActions.GetCurrentPerson());
        });
    }

    public ngAfterViewInit(): void {
        setTimeout(() => {
            this.updateTabIndicator(0);
        }, 100);

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === "attributes" && mutation.attributeName === "style") {
                    this.updateTabIndicator(this.getSelectedTabIndex());
                }
            });
        });

        const tabMenu = this.elementRef.nativeElement.querySelector(".root__tab-menu");
        if (tabMenu) {
            observer.observe(tabMenu, {
                attributes: true,
                subtree: true,
            });
        }

        this.setupResizeObserver();

        this.ngZone.runOutsideAngular(() => {
            fromEvent(window, "resize").pipe(debounceTime(100), untilDestroyed(this)).subscribe(() => {
                this.ngZone.run(() => {
                    this.updateTabIndicator(this.getSelectedTabIndex());
                });
            });
        });

        this.tabItems.changes.pipe(untilDestroyed(this)).subscribe(() => {
            this.updateTabIndicator(this.getSelectedTabIndex());
        });
    }

    public ngOnDestroy(): void {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
    }

    private setupResizeObserver(): void {
        if (typeof ResizeObserver !== "undefined") {
            this.resizeObserver = new ResizeObserver((entries) => {
                this.ngZone.run(() => {
                    this.updateTabIndicator(this.getSelectedTabIndex());
                });
            });

            const tabMenu = this.elementRef.nativeElement.querySelector(".root__tab-menu");
            if (tabMenu) {
                this.resizeObserver.observe(tabMenu);
            }
        }
    }

    private getSelectedTabIndex(): number {
        switch (this.selectedListType) {
            case PermitListType.Ongoing:
                return 0;
            case PermitListType.Planned:
                return 1;
            case PermitListType.Completed:
                return 2;
            default:
                return 0;
        }
    }

    private getRemSize(): number {
        return parseFloat(getComputedStyle(document.documentElement).fontSize);
    }

    private updateTabIndicator(index: number): void {
        const tabs = this.tabItems.toArray();
        if (!tabs.length || index >= tabs.length) {
            return;
        }

        const tabMenu = this.elementRef.nativeElement.querySelector(".root__tab-menu");
        if (!tabMenu) {
            return;
        }

        const menuRect = tabMenu.getBoundingClientRect();
        const remSize = this.getRemSize();

        const availableWidth = menuRect.width - (remSize); // Total width minus padding on both sides
        const indicatorWidth = availableWidth / 3; // Divide by number of tabs

        const leftOffset = indicatorWidth * index;

        this.tabIndicatorState = {
            value: index,
            params: {
                transform: `translateX(${leftOffset}px)`,
                width: indicatorWidth,
            },
        };
        if (index != 0) {
            this.tabItems.toArray()[0].nativeElement.classList.remove("item--initial");
        }
        this.cdr.detectChanges();
    }

    public changeTab(listType: PermitListType): void {
        this.selectedListType = listType;

        if (listType == PermitListType.Completed) {
            this.store.dispatch(new PermitListActions.GetAllCompleted(0, 50));
        }

        const index = this.getSelectedTabIndex();
        this.updateTabIndicator(index);
    }

    public preparePermit(): void {
        this.store.dispatch(new Navigate([PermitPaths.Root, PermitPaths.Prepare]));
    }

    public showSubscriptionPermits(event: MouseEvent): void {
        event.preventDefault();
        this.showAllSubscriptionPermits = !this.showAllSubscriptionPermits;
        // Save preference to localStorage
        localStorage.setItem('showSubscriptionPermits', JSON.stringify(this.showAllSubscriptionPermits));
        this.loading$.next(true);
        this.store.dispatch(new PermitListActions.ToggleShowAllPermits()).subscribe(() => {
            if (!this.showAllSubscriptionPermits) {
                this.store.dispatch(new PermitListActions.UpdateFilter([]));
            }

            this.store.dispatch(new PermitListActions.GetAllOngoing()).subscribe(() => {
                this.loading$.next(false);
            });
        });
    }

    public toggleFilter(event: MouseEvent): void {
        event.stopPropagation();

        this.modalService.open(PermitRootFilterModalComponent, true, <PermitRootFilterModalComponent> {});
    }

    public openConnectionRequest(): void {
        var invitations = this.store.selectSnapshot(SubscriptionsSelectors.invitations);
        if (invitations && invitations.length > 0) {
            this.modalService.open(
                SubscriptionInvitationAnswerComponent,
                true,
                <SubscriptionInviteAnswerComponentData> { invitation: invitations[0] },
            );
        }
    }

    public openPermitRequest(): void {
        this.store.dispatch(new Navigate([PermitPaths.Root, PermitPaths.PermitRequests]));
    }

    public signPermit(permit: PermitToSign, claim: boolean = false) {
        const currentPerson = this.store.selectSnapshot(PermitUserSelectors.currentPerson);

        var permitsToSign: PermitToSign[];
        if (claim) {
            permitsToSign = this.store.selectSnapshot(PermitUserSelectors.permitsToClaim);
        } else {
            permitsToSign = this.store.selectSnapshot(PermitUserSelectors.permitsToSign);
        }

        const allRolesForPermit = permitsToSign.filter((p) => p.permitId === permit.permitId).map((p) => p.role);

        this.modalService.open(
            SignPersonModalComponent,
            true,
            <SignPersonModalComponentData> {
                person: currentPerson,
                permitId: permit.permitId,
                roles: allRolesForPermit,
                name: `${currentPerson.firstName} ${currentPerson.lastName}`,
            },
        );
    }

    openCreateModal(mode: PermitCreateMode): void {
        this.modalService.open(PermitCreateModalComponent, true, <PermitCreateModalData> { mode }, "both");
    }

    onCreatePermit(
        event: MouseEvent,
        isPremium: boolean,
        isDemo: boolean,
        permitType: PermitType = PermitType.hotWork,
    ): void {
        event.stopPropagation();
        this.store.dispatch(new PermitStepActions.StartCreate(isPremium, isDemo));
        this.store.dispatch(new PermitCreateActions.CreatePermit(isPremium, isDemo, permitType));
        this.store.dispatch(new PermitQuestionActions.ResetToDefault());
        this.loading$.next(true);
        this.actions$.pipe(ofActionSuccessful(PermitCreateActions.CreatePermit)).pipe(untilDestroyed(this)).subscribe(
            () => {
                this.loading$.next(false);
            },
        );
    }
}
